import { PROCEDURE_STATUS, TExamProcedureStatusRequirement, TExamStepLearner } from "@/api/services/main/exam-steps/dtos/get-exam-step-learners.dto";
import { Table, TableSorting } from "@/components/elements/Table";
import { DATE_FORMAT, YEAR_MONTH_FORMAT } from "@/constants/datetime";
import { EMPTY_STR } from "@/constants/string";
import { useTable } from "@/hooks/useTable";
import { useInitSetup } from "@/providers/InitSetupProvider";
import { usePermissions } from "@/api/services/main/auth";
import { black, divider } from "@/theme/colors";
import { EXAM_METHOD, USER_PERMISSION, USER_TYPE } from "@/types/enum";
import { dayjs } from "@/utils/dayjs";
import { truncateWithEllipsis } from "@/utils/string";
import { Box, Stack, Tooltip, Typography } from "@mui/material";
import { MRT_ColumnDef } from "material-react-table";
import { FC, useMemo } from "react";
import { ProcedureStatusCell } from "@/features/exam-steps/components/ProcedureStatusTableCell";
import { RequirementCheckboxCell } from "@/features/exam-steps/components/RequirementCheckboxTableCell";
import { IconActionOutline } from "@/assets/icons";

type TExamLearnerTableProps = {
  industryId: number;
  rows: TExamStepLearner[];
  examRequirements?: TExamProcedureStatusRequirement[];
  isLoading?: boolean;
};

const truncateText = (value: string | null | undefined) => {
  if (!value) return EMPTY_STR.TEXT;
  return truncateWithEllipsis(value, 25);
};

export const ExamLearnerTable: FC<TExamLearnerTableProps> = ({ industryId, rows, examRequirements, isLoading }) => {
  const { userType } = useInitSetup();
  const { hasPermission } = usePermissions();
  const isRsoUser = userType === USER_TYPE.RSO_USER;
  const canEditExamStep = hasPermission(USER_PERMISSION.EXAM_STEP_EDIT);

  // 受験要件ごとに動的なチェックボックス列を生成 (displayOrder 昇順)。
  const requirementColumns = useMemo<MRT_ColumnDef<TExamStepLearner>[]>(
    () =>
      [...(examRequirements ?? [])]
        .sort((a, b) => a.displayOrder - b.displayOrder)
        .map((requirement) => ({
          id: `requirement_${requirement.id}`,
          header: requirement.name,
          accessorFn: (row: TExamStepLearner) => (row.examRequirementIds ?? []).includes(requirement.id),
          size: 162,
          // overflow を visible にして並ぶソートアイコンが Content-Wrapper の clip で消えないようにする。
          muiTableHeadCellProps: {
            sx: { "& .Mui-TableHeadCell-Content-Wrapper": { overflow: "visible" } },
          },
          Header: (
            <Box display="flex" alignItems="center">
              <Typography variant="sub16Bold">{truncateWithEllipsis(requirement.name, 8)}</Typography>
              {requirement.description && (
                <Stack mr={0.5}>
                  <Tooltip title={requirement.description ?? ""} arrow>
                    <IconActionOutline color={black[80]} />
                  </Tooltip>
                </Stack>
              )}
              <TableSorting name={`examRequirement.${requirement.id}`}></TableSorting>
            </Box>
          ),
          Cell: ({ row }: { row: { original: TExamStepLearner } }) => (
            <Stack alignItems="center">
              <RequirementCheckboxCell
                industryId={industryId}
                learnerId={row.original.id}
                requirementId={requirement.id}
                checked={(row.original.examRequirementIds ?? []).includes(requirement.id)}
                isDisabledHandling={!canEditExamStep}
              />
            </Stack>
          ),
        })),
    [examRequirements, industryId, canEditExamStep],
  );

  const columns = useMemo<MRT_ColumnDef<TExamStepLearner>[]>(
    () => [
      {
        id: "fullName",
        header: "氏名",
        accessorKey: "fullName",
        Cell: ({ row }) => truncateText(row.original.fullName),
        Header: <TableSorting name="learner.fullName">氏名</TableSorting>,
      },
      ...(isRsoUser
        ? [
          {
            id: "affiliatedOrganization",
            header: "所属機関",
            accessorKey: "affiliatedOrganization.companyName",
            maxSize: 158,
            Cell: ({ row }: { row: { original: TExamStepLearner } }) =>
              truncateText(row.original.affiliatedOrganization?.companyName),
            Header: <TableSorting name="affiliatedOrganization.companyName">所属機関</TableSorting>,
          },
        ]
        : []),
      {
        id: "nationality",
        header: "国籍",
        accessorKey: "nationality.nationalityName",
        maxSize: 116,
        Cell: ({ row }) => truncateText(row.original.nationality?.nationalityName),
        Header: <TableSorting name="nationality.nationalityName">国籍</TableSorting>,
      },
      {
        id: "currentStatus",
        header: "手続ステータス",
        accessorKey: "currentStatus.key",
        maxSize: 208,
        Cell: ({ row }) => {
          const learner = row.original;
          const options = (learner.statusOptions ?? []).map((o) => ({
            value: o.key,
            label: o.label,
          }));
          return (
            <Stack py={1} px={2}>
              <ProcedureStatusCell
                industryId={industryId}
                learnerId={learner.id}
                value={learner.currentStatus?.key ?? ""}
                options={options}
                isDisabledHandling={!canEditExamStep}
              />
            </Stack>
          );
        },
        Header: <TableSorting name="currentStatus.key">手続ステータス</TableSorting>,
      },
      {
        id: "nextProcedureEndDate",
        header: "次の手続期日・期限",
        accessorKey: "nextProcedureEndDate",
        maxSize: 180,
        Cell: ({ row }) => {
          const learner = row.original;
          const statusKey = learner.currentStatus?.key;
          const shouldHide =
            statusKey === PROCEDURE_STATUS.NEW_REGISTRATION ||
            statusKey === PROCEDURE_STATUS.COMPLETED ||
            statusKey === PROCEDURE_STATUS.OUT_OF_SCOPE;
          if (shouldHide) return EMPTY_STR.TEXT;
          if (learner.examMethod === EXAM_METHOD.CBT) {
            return learner.nextProcedureDuration || EMPTY_STR.TEXT;
          }
          const date = dayjs(learner.nextProcedureEndDate);
          return date.isValid() ? `${date.format(DATE_FORMAT)}まで` : EMPTY_STR.TEXT;
        },
        Header: <TableSorting name="nextProcedureEndDate">次の手続期日・期限</TableSorting>,
      },
      {
        id: "examMonth",
        header: "試験年月",
        accessorKey: "examMonth",
        maxSize: 120,
        Cell: ({ row }) => {
          const raw = row.original.examMonth;
          if (!raw) return EMPTY_STR.TEXT;
          const date = dayjs(raw, ["YYYY/MM", "YYYY-MM", "YYYY/MM/DD", "YYYY-MM-DD"]);
          return date.isValid() ? date.format(YEAR_MONTH_FORMAT) : raw;
        },
        Header: <TableSorting name="examMonth">試験年月</TableSorting>,
      },
      {
        id: "remainingPeriod",
        header: "1号残期間",
        accessorKey: "remainingPeriod",
        size: 129,
        Cell: ({ row }) => row.original.remainingPeriod ?? EMPTY_STR.TEXT,
        Header: <TableSorting name="remainingPeriod">1号残期間</TableSorting>,
      },
      {
        id: "prefecture",
        header: "居住地",
        accessorKey: "prefecture",
        size: 106,
        Cell: ({ row }) => truncateText(row.original?.prefecture) ?? EMPTY_STR.TEXT,
        Header: <TableSorting name="learner.prefecture">居住地</TableSorting>,
      },
      ...requirementColumns,
      {
        id: "registeredAt",
        header: "登録日",
        accessorKey: "registeredAt",
        maxSize: 120,
        Cell: ({ row }) => {
          const date = dayjs(row.original.registeredAt);
          return date.isValid() ? date.format(DATE_FORMAT) : EMPTY_STR.TEXT;
        },
        Header: <TableSorting name="learner.createdAt">登録日</TableSorting>,
      },
    ],
    [isRsoUser, userType, industryId, canEditExamStep, requirementColumns],
  );

  // 受験要件列は API から非同期で届くため、MRT が内部 columnOrder を初回 (列が無い状態) で
  // 確定してしまい、後から来た列を末尾に追加してしまう。columns 配列の順序で columnOrder を
  // 制御し、登録日の前に固定する。
  const columnOrder = useMemo(
    () => columns.map((column) => column.id).filter((id): id is string => Boolean(id)),
    [columns]
  );

  const { table } = useTable({
    columns,
    rows,
    state: { columnOrder },
    getRowId: (row) => row.id.toString(),
    renderEmptyRowsFallback: () => <Box />,
  });

  return (
    <Table
      table={table}
      isLoading={isLoading}
      minHeight={150}
      borderColor={divider.high}
    />
  );
};
